import React, {useEffect} from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import GoogleMap from './GoogleMap';

const {FiPhone, FiMail, FiMapPin, FiClock} = FiIcons;

const ContactPage = () => {
  useEffect(() => {
    // Skript-Integration: https://embed.formaloo.me/v1/main.js
    const scriptId = 'formaloo-script';

    // Altes Skript entfernen, falls vorhanden (Clean Start)
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    // Neues Skript erstellen
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://embed.formaloo.me/v1/main.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup beim Verlassen der Seite
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Section with Background Video */}
      <section className="relative py-32 bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0">
          <motion.video
            initial={{opacity: 0}}
            animate={{opacity: 0.6}}
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source
              src="https://canv.wasacon.com/rudt-bau/_assets/video/d24b073b5b6950752e69527999cf0409.mp4"
              type="video/mp4"
            />
          </motion.video>
        </div>

        {/* Enhanced Dark Overlay for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
              Kontakt
            </h1>
            <p className="text-2xl md:text-3xl mb-12 max-w-5xl mx-auto leading-relaxed font-light">
              Lassen Sie uns über Ihr Bauvorhaben sprechen. Wir freuen uns auf Ihre Anfrage und beraten Sie gerne.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+4924528607019"
                className="inline-flex items-center space-x-3 bg-[#3466ad] text-white px-10 py-5 rounded-xl text-xl font-semibold hover:bg-[#2a5291] transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
              >
                <SafeIcon icon={FiPhone} className="w-6 h-6" />
                <span>02452 860 70 19</span>
              </a>
              <a
                href="mailto:info@rudtbau.de"
                className="inline-flex items-center space-x-3 border-2 border-white text-white px-10 py-5 rounded-xl text-xl font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                <SafeIcon icon={FiMail} className="w-6 h-6" />
                <span>info@rudtbau.de</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <motion.div
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#3466ad] rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiPhone} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Telefon</h3>
              <a
                href="tel:+4924528607019"
                className="text-gray-600 hover:text-[#3466ad] transition-colors"
              >
                02452 860 70 19
              </a>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8, delay: 0.1}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#3466ad] rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMail} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">E-Mail</h3>
              <a
                href="mailto:info@rudtbau.de"
                className="text-gray-600 hover:text-[#3466ad] transition-colors"
              >
                info@rudtbau.de
              </a>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8, delay: 0.2}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#3466ad] rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiMapPin} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Adresse</h3>
              <p className="text-gray-600">
                Wassenbergerstr. 41<br />
                52525 Heinsberg
              </p>
            </motion.div>

            <motion.div
              initial={{opacity: 0, y: 30}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{duration: 0.8, delay: 0.3}}
              className="text-center"
            >
              <div className="w-16 h-16 bg-[#3466ad] rounded-xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiClock} className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Öffnungszeiten</h3>
              <p className="text-gray-600">
                Mo - Fr: 7:00 - 17:00<br />
                Sa: 8:00 - 12:00
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-white min-h-[850px] flex flex-col relative z-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full flex-grow flex flex-col">
          <motion.div
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8, delay: 0.2}}
            className="w-full h-full flex-grow"
          >
            {/* Formaloo Formular Integration */}
            <div
              className="formaloo--root-container"
              data-form-slug="WCMU0Rwt"
              style={{width: '100%', height: '100%'}}
            ></div>
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.8}}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">So finden Sie uns</h2>
            <p className="text-lg text-gray-600">
              Besuchen Sie uns in unserem Büro in Heinsberg oder vereinbaren Sie einen Termin vor Ort bei Ihrem Bauvorhaben.
            </p>
          </motion.div>
          <GoogleMap />
        </div>
      </section>
    </div>
  );
};

export default ContactPage;